/**
 * ProtectedRoute — Guards auth-gated routes the same way iOS uses
 * NavigationStack guards on the Coordinator pattern.
 *
 * Rules:
 *  1. If auth is still loading → show nothing (splash already handles UI)
 *  2. If no firebase user → redirect to /login
 *  3. If profile is incomplete → redirect to /setup
 *  4. Otherwise → render children
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useUserData } from './UserDataContext';
import { PATHS } from '../router/paths';
import { OfflineScreen } from './OfflineScreen';
import { consumePendingGroupInvite } from '../utils/inviteCode';

// True when the device is offline OR a session was previously established but
// auth couldn't be confirmed (slow/dropped connection). In either case we must
// not bounce the user to login — that's the false "logout" being fixed.
const hadSession = () => {
  try { return localStorage.getItem('afroyap_had_session') === '1'; } catch { return false; }
};
const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

const isProfileSetupComplete = (profile: any): boolean => {
  if (!profile) return false;
  
  // 1. Explicit flag (authoritative — in BOTH directions)
  if (profile.setupComplete === true) return true;
  // An account created by a flow that sets this to false is mid-setup, and the
  // leniency rule below must not release it early. That is what let Google
  // signups onto the platform after the very first onboarding step: Google
  // supplies name and username up front, so the moment the media step added a
  // photo the three leniency conditions were all true and the gate opened with
  // three steps still unanswered. Legacy accounts have this undefined, not
  // false, so they are unaffected.
  if (profile.setupComplete === false) return false;

  // 2. High-trust ranks skip setup block
  if (['legion', 'union', 'watcher', 'verified'].includes(profile.rank)) return true;

  // 3. Robust checks for standard users
  const hasPhotos = Array.isArray(profile.portfolio) && profile.portfolio.some((p: any) => p && String(p).trim() !== '');
  const hasIdentities = (Array.isArray(profile.identities) && profile.identities.length >= 1) ||
                        (Array.isArray(profile.identityTags)  && profile.identityTags.length >= 1);
  const hasName     = !!(profile.name     && profile.name.trim());
  const hasUsername = !!(profile.username && profile.username.trim());
  const hasDOB      = !!(profile.dateOfBirth && profile.dateOfBirth.trim());
  
  // 🔒 LENIENCY: If they have a username AND a name AND at least one photo, 
  // they are "setup enough" to browse. This prevents locking out legacy users 
  // who might be missing newer fields like 'countryFlag' or 'identities'.
  if (hasUsername && hasName && hasPhotos) return true;

  // Otherwise, fallback to strict check for new users
  const hasCountry  = !!(profile.country   && profile.country.trim());
  const hasHeritage = !!(profile.countryFlag && profile.countryFlag.trim());

  return hasPhotos && hasIdentities && hasName && hasUsername && hasDOB && hasCountry && hasHeritage;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { firebaseUser, authLoading, profileSettled, profileLoaded, userProfile } = useUserData();
  const location = useLocation();

  // Still booting — let App.tsx splash screen handle the UI
  if (authLoading) return null;

  // Auth couldn't be confirmed. If the user previously had a session or the
  // device is offline, this is a connectivity problem, not a logout — show the
  // offline screen so they can reconnect/reload instead of being kicked to login
  // (hostile to users who don't remember their credentials).
  if (!firebaseUser) {
    // Two different problems wearing one screen until now. Offline is a network
    // fault and reloading fixes it; a session that cannot be confirmed while the
    // device is online is an auth fault, and telling that user to check their
    // Wi-Fi sends them looking for a problem that is not there.
    if (isOffline()) return <OfflineScreen variant="offline" />;
    if (hadSession()) return <OfflineScreen variant="session" />;
    return <Navigate to={PATHS.LOGIN} state={{ from: location }} replace />;
  }

  // Profile snapshot hasn't returned yet — don't redirect to /setup prematurely.
  // This was the source of the "back to onboarding screen" race: a slow Firestore
  // read meant the empty default profile failed the setup-complete check and
  // bounced the user to the setup screen even though they were already onboarded.
  // If we're offline and the cached profile never arrives, surface the offline
  // screen rather than a blank screen.
  if (!profileSettled) return isOffline() ? <OfflineScreen variant="offline" /> : null;

  // Logged in but profile incomplete → send to setup. BUT only when we actually
  // loaded the profile document. If the read failed (offline/slow), the profile
  // is empty/default — that's a connectivity problem, not an incomplete account,
  // so show the offline screen instead of wrongly forcing setup.
  if (!isProfileSetupComplete(userProfile)) {
    // Signed in and online, but the profile document never arrived. Not a
    // network fault and not a login fault, so it claims to be neither.
    if (!profileLoaded && isOffline()) return <OfflineScreen variant="offline" />;
    if (!profileLoaded && hadSession()) return <OfflineScreen variant="error" />;
    return <Navigate to={PATHS.PROFILE_SETUP} replace />;
  }

  return <>{children}</>;
}

/** Reverse guard: prevents logged-in users from seeing onboarding/login/signup. */
export function PublicOnlyRoute({ children }: ProtectedRouteProps) {
  const { firebaseUser, authLoading, profileSettled, userProfile } = useUserData();
  const location = useLocation();

  if (authLoading) return null;
  if (firebaseUser && !profileSettled) return null;

  if (firebaseUser && isProfileSetupComplete(userProfile)) {
    // Honour a pending tribe/forum invite (from the public invite link) so an
    // EXISTING user who taps "Log in" lands on the group — not Explore. (The
    // signup path consumes this in ProfileSetup; this covers returning users.)
    const pendingPath = consumePendingGroupInvite();
    if (pendingPath) return <Navigate to={pendingPath} replace />;
    return <Navigate to={PATHS.EXPLORE} replace />;
  }

  return <>{children}</>;
}
